import { UiLabel } from 'hx-services';
import { EditorRejectionRequest } from '@callcenter-app/service/order-editor.service';

/**
 * The refusals of a conversation (design §3.4, E24): what the client wanted and did not get — a product, a time of the pickup or of the delivery, an address, a promo code, the decoration. One record
 * per conversation: the first refusal writes it (`POST`), every next state of the list is `PUT` over the same record — the whole state, so a refusal taken off with its cross is a shorter list and an
 * empty state takes the record off. This module is the state the page keeps (and the draft stores) and the request made of it. Pure.
 *
 * Every refusal is told on the screen as a title and a reason («Красный бархат max — свободного на 2 октября нет»): the reason is a code of the dictionary and its parameters, kept with the refusal so that
 * it is told in the language of the screen whenever it is read.
 */

export type RejectionKind = 'product' | 'pickupTime' | 'deliveryTime' | 'address' | 'promo' | 'decor';

/** `orderEditor.rejections.reason.<code>` and what it needs to be put into words. */
export interface RejectionReason {
  code: string;
  params?: Record<string, string | number>;
}

export interface RejectionItem {
  kind: RejectionKind;
  /** A product: its id; a time: `HH:mm`; an address: the text as it was typed; a promo code: the code; the decoration: empty. */
  value: string;
  /** A product only: its name as the screen had it. */
  title?: UiLabel;
  reason?: RejectionReason;
}

export interface RejectionDraft {
  /** The record of E24, once the first refusal was written. */
  id?: number;
  items: RejectionItem[];
  /** «Почему клиент отказался — своими словами». */
  note: string;
  /** The server does not hold this state yet (the request did not go through, or is on its way): the next request sends all of it. */
  dirty?: boolean;
}

export const EMPTY_REJECTION: RejectionDraft = { items: [], note: '' };

export const REJECTION_NOTE_LIMIT = 500;
export const REJECTION_TEXT_LIMIT = 300;
/** The server refuses (`field.wrongLength`) an address of a refusal longer than 200 characters and a promo code longer than 100. */
export const REJECTION_ADDRESS_LIMIT = 200;
export const REJECTION_PROMO_LIMIT = 100;
/** Every list of the request is limited by the server to 50 (the products to 100): forty refusals in all can never break one of them. */
export const REJECTION_ITEMS_LIMIT = 40;

/** The longest text a refusal of this kind may hold. */
export function textLimitOf(kind: RejectionKind): number {
  return kind === 'address' ? REJECTION_ADDRESS_LIMIT : kind === 'promo' ? REJECTION_PROMO_LIMIT : REJECTION_TEXT_LIMIT;
}

/** Where and for whom the refusals are written: the day, the city, the store (when one is chosen) and the client's number (when it is complete). */
export interface RejectionScope {
  date: string;
  cityId: number;
  storeId?: number;
  phone?: string;
}

export function itemKey(item: Pick<RejectionItem, 'kind' | 'value'>): string {
  return `${item.kind}:${item.value}`;
}

/** The refusal is written again for the same thing: its reason is renewed, nothing is added. A text longer than its kind may hold is cut (the server would turn the whole state down for it). */
export function withItem(draft: RejectionDraft, wanted: RejectionItem): RejectionDraft {
  const item = wanted.value.length > textLimitOf(wanted.kind) ? { ...wanted, value: wanted.value.slice(0, textLimitOf(wanted.kind)) } : wanted;
  const key = itemKey(item);
  const seen = draft.items.some(i => itemKey(i) === key);
  if (!seen && draft.items.length >= REJECTION_ITEMS_LIMIT) {
    return draft;
  }
  const items = seen ? draft.items.map(i => (itemKey(i) === key ? item : i)) : [...draft.items, item];
  return { ...draft, items, dirty: true };
}

export function withoutItem(draft: RejectionDraft, key: string): RejectionDraft {
  return draft.items.some(i => itemKey(i) === key) ? { ...draft, items: draft.items.filter(i => itemKey(i) !== key), dirty: true } : draft;
}

export function withNote(draft: RejectionDraft, note: string): RejectionDraft {
  const next = note.trim().slice(0, REJECTION_NOTE_LIMIT);
  return next === draft.note ? draft : { ...draft, note: next, dirty: true };
}

/** Nothing is written: no refusal and no reason. */
export function isEmptyRejection(draft: RejectionDraft | undefined): boolean {
  return !draft || (draft.items.length === 0 && draft.note.trim() === '');
}

/** The id of the record an order is to be tied to (E21 `rejectionId`): only one that holds something. */
export function rejectionIdOf(draft: RejectionDraft | undefined): number | undefined {
  return draft && draft.id !== undefined && !isEmptyRejection(draft) ? draft.id : undefined;
}

/** E24: the whole state. A refusal at the pickup is written with `pickup`, at the delivery (its time, its address) with `delivery`. */
export function rejectionRequestOf(draft: RejectionDraft, scope: RejectionScope): EditorRejectionRequest {
  const values = (kind: RejectionKind): string[] => draft.items.filter(i => i.kind === kind).map(i => i.value);
  const productInfoIds = values('product').map(Number).filter(n => Number.isFinite(n));
  const pickupTimes = values('pickupTime');
  const deliveryTimes = values('deliveryTime');
  const addresses = values('address');
  const note = draft.note.trim();
  return {
    date: scope.date,
    cityId: scope.cityId,
    ...(scope.storeId !== undefined ? { storeId: scope.storeId } : {}),
    ...(scope.phone ? { phone: scope.phone } : {}),
    productInfoIds,
    pickupTimes,
    deliveryTimes,
    addresses,
    promos: values('promo'),
    decor: draft.items.some(i => i.kind === 'decor'),
    pickup: pickupTimes.length > 0,
    delivery: deliveryTimes.length > 0 || addresses.length > 0,
    ...(note ? { note } : {}),
  };
}

/** `9:30` → `09:30`; anything that is not a time of the day is none. */
export function normalizeTime(text: string): string | undefined {
  const m = /^(\d{1,2})[:.]?(\d{2})$/.exec(text.trim());
  if (!m) {
    return undefined;
  }
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h <= 23 && min <= 59 ? `${String(h).padStart(2, '0')}:${m[2]}` : undefined;
}

// ---------- the storage ----------

type Dict = Record<string, unknown>;

function isDict(value: unknown): value is Dict {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const KINDS: readonly RejectionKind[] = ['product', 'pickupTime', 'deliveryTime', 'address', 'promo', 'decor'];

function cleanLabel(value: unknown): UiLabel | undefined {
  if (!isDict(value)) {
    return undefined;
  }
  const label: UiLabel = {};
  (['ru', 'kk', 'en', 'uz', 'tg'] as const).forEach(lang => {
    const text = value[lang];
    if (typeof text === 'string') {
      label[lang] = text.slice(0, 300);
    }
  });
  return Object.keys(label).length ? label : undefined;
}

function cleanReason(value: unknown): RejectionReason | undefined {
  if (!isDict(value) || typeof value['code'] !== 'string' || !/^[A-Za-z0-9]{1,40}$/.test(value['code'])) {
    return undefined;
  }
  const params: Record<string, string | number> = {};
  if (isDict(value['params'])) {
    Object.entries(value['params']).forEach(([name, v]) => {
      if (/^[A-Za-z0-9]{1,20}$/.test(name) && (typeof v === 'string' || (typeof v === 'number' && Number.isFinite(v)))) {
        params[name] = typeof v === 'string' ? v.slice(0, 300) : v;
      }
    });
  }
  return { code: value['code'], ...(Object.keys(params).length ? { params } : {}) };
}

function cleanItem(value: unknown): RejectionItem | undefined {
  if (!isDict(value)) {
    return undefined;
  }
  const kind = KINDS.find(k => k === value['kind']);
  if (!kind) {
    return undefined;
  }
  const raw = typeof value['value'] === 'string' ? value['value'].slice(0, textLimitOf(kind)) : '';
  if ((kind !== 'decor' && raw === '') || (kind === 'product' && !/^\d+$/.test(raw))) {
    return undefined;
  }
  if ((kind === 'pickupTime' || kind === 'deliveryTime') && !normalizeTime(raw)) {
    return undefined;
  }
  const title = kind === 'product' ? cleanLabel(value['title']) : undefined;
  const reason = cleanReason(value['reason']);
  return { kind, value: kind === 'decor' ? '' : raw, ...(title ? { title } : {}), ...(reason ? { reason } : {}) };
}

/** A refusal state that came back from the storage: every piece checked and cut — a stored value is never trusted as it is. */
export function cleanRejectionDraft(value: unknown): RejectionDraft | undefined {
  if (!isDict(value)) {
    return undefined;
  }
  const items: RejectionItem[] = [];
  (Array.isArray(value['items']) ? value['items'] : []).forEach(raw => {
    const item = cleanItem(raw);
    if (item && !items.some(i => itemKey(i) === itemKey(item)) && items.length < REJECTION_ITEMS_LIMIT) {
      items.push(item);
    }
  });
  const note = typeof value['note'] === 'string' ? value['note'].slice(0, REJECTION_NOTE_LIMIT) : '';
  const id = typeof value['id'] === 'number' && Number.isFinite(value['id']) && value['id'] > 0 ? value['id'] : undefined;
  if (items.length === 0 && note.trim() === '' && id === undefined) {
    return undefined;
  }
  return { ...(id !== undefined ? { id } : {}), items, note, ...(value['dirty'] === true ? { dirty: true } : {}) };
}
